import type { AngebotStatus, AuftragStatus, RechnungAnzeigeStatus, SupplierContract,
              Zahlstatus } from "@kundeninfo/api-client";

/**
 * Die STATUS-FILTER der vier Objektbereiche — ihre Namen in der Adresse, ihre Abbildung
 * auf den Vertrag und der Umgang mit dem Adress-Segment, das sie trägt.
 *
 * Diese Datei löst `rechnungsfilter.ts` ab (Paket „Filterkopf statt zweiter Spalte",
 * Entscheid Dirk 07.09.2026). Dort stand dieselbe Sache für EINEN Bereich; seit die
 * Filterung in allen Objektbereichen als Chip-Leiste im Filterkopf steht und in der
 * ADRESSE mitreist, brauchen sie alle dieselbe Tabelle und denselben Kodierer.
 *
 * **Warum eine eigene kleine Datei und nicht die Ansichten selbst:** Der ROUTER braucht die
 * Namen (sie stehen in der Adresse, `/rechnungen/bezahlt`), und die Ansichten brauchen sie
 * ebenso. Stünden sie in views/BereichRechnungen.tsx, importierte der Router eine Ansicht,
 * die ihrerseits den Router einbindet (`VerlassenWaechter`) — ein Ringschluss, der zur
 * Laufzeit erst auffiele. Dieselbe Trennung wie bei `vorgangsstatus.ts`.
 *
 * **Die Namen sind deutsch und kleingeschrieben** wie die Bereichsnamen der Verwaltung
 * (`/verwaltung/nummernkreise`) — die Adresse dieses Systems spricht deutsch, während der
 * VERTRAG (`status`/`anzeigeStatus`) englisch bleibt. Die Zuordnung steht hier, an genau
 * einer Stelle.
 *
 * **Was NICHT in der Adresse steht:** Suchtext, Blätterung und die Zusatzfilter des
 * Filterkopfs (Unternehmen; beim Artikel zusätzlich die Kategorie). Der Statusfilter ist
 * ein BILDSCHIRM — „die abgelehnten Angebote" ist ein Ort, an den ein Lesezeichen zeigen
 * darf. Ein Suchtext ist es nicht („Seite 7 bei Suchtext X" ist morgen eine andere Seite
 * 7), und die Artikel-Kategorien stehen erst zur Laufzeit fest: Sie kommen vom Server, und
 * ein Adressbestandteil aus einer Menge, die der Router nicht kennt, liesse sich weder
 * prüfen noch von einer Objektkennung unterscheiden.
 */

/** Das reservierte Segment für „kein Status eingeschaltet" (nur mehrfach wählbare Leisten
 *  können dort landen — siehe `segmentFuerFilter`). Ohne es wäre genau der eine Zustand,
 *  den die Chip-Leiste zulässt, nicht adressierbar; mit ihm gilt weiter: EIN Bildschirm,
 *  EINE Adresse. Es kann mit keinem Filternamen zusammenfallen — die drei Tabellen unten
 *  führen keinen Eintrag „keine" (festgehalten in test/bereichsfilter.test.ts). */
const KEINE = "keine";

/** Ein Filtereintrag: sein Name in der Adresse und der Status, für den er steht. */
export type Filtereintrag<I extends string, S extends string> = {
  readonly id: I;
  readonly status: S;
};

// ------------------------------------------------------------------ die drei Tabellen

export type AngebotsFilter =
  "entwurf" | "gesendet" | "angenommen" | "abgelehnt" | "zurueckgezogen";

/** Die Einträge in der Reihenfolge des Lebenslaufs — dieselbe wie in
 *  `ANGEBOT_STATUS_LABEL`. Die BESCHRIFTUNG steht bewusst nicht hier: Sie kommt aus der
 *  Beschriftungstabelle (vorgangsstatus.ts), und eine zweite Fassung daneben liefe beim
 *  ersten Umbenennen auseinander. */
export const ANGEBOTS_FILTER: readonly Filtereintrag<AngebotsFilter, AngebotStatus>[] = [
  { id: "entwurf", status: "DRAFT" },
  { id: "gesendet", status: "SENT" },
  { id: "angenommen", status: "ACCEPTED" },
  { id: "abgelehnt", status: "REJECTED" },
  { id: "zurueckgezogen", status: "WITHDRAWN" },
];

export type AuftragsFilter = "aktiv" | "abgeschlossen" | "storniert";

export const AUFTRAGS_FILTER: readonly Filtereintrag<AuftragsFilter, AuftragStatus>[] = [
  { id: "aktiv", status: "ACTIVE" },
  { id: "abgeschlossen", status: "COMPLETED" },
  { id: "storniert", status: "CANCELLED" },
];

export type RechnungsFilter = "entwurf" | "festgeschrieben" | "bezahlt" | "storniert";

export const RECHNUNGS_FILTER:
  readonly Filtereintrag<RechnungsFilter, RechnungAnzeigeStatus>[] = [
  { id: "entwurf", status: "DRAFT" },
  { id: "festgeschrieben", status: "ISSUED" },
  { id: "bezahlt", status: "PAID" },
  { id: "storniert", status: "CANCELLED" },
];

/*
 * Die zwei LIEFERANTENBEREICHE (Final-Fixrunde Lieferanten, A-W3): Ihre Status-Chips reisen in der Adresse wie die der
 * Rechnungen (`/liefervertraege/gekuendigt`, `/eingangsrechnungen/ueberfaellig,faellig`), damit der Rückweg aus der Akte
 * dieselbe Abfrage findet. Anders als dort heißt hier KEIN Chip „alle“ (der Chip „Alle“ der Leiste): kein Segment.
 */
export type SupplierContractFilter = "laufend" | "gekuendigt" | "beendet";

export const SUPPLIER_CONTRACT_FILTER:
  readonly Filtereintrag<SupplierContractFilter, Exclude<SupplierContract["state"], "PLANNED">>[] = [
  { id: "laufend", status: "RUNNING" },
  { id: "gekuendigt", status: "TERMINATED" },
  { id: "beendet", status: "ENDED" },
];

export type IncomingFilter =
  "ueberfaellig" | "faellig" | "teilbezahlt" | "guthaben" | "offen" | "angenommen" | "bezahlt" | "storniert";

/** In der Ordnung der Chips: das Schlechteste vorn (Hausregel). */
export const INCOMING_FILTER: readonly Filtereintrag<IncomingFilter, Zahlstatus["state"]>[] = [
  { id: "ueberfaellig", status: "OVERDUE" },
  { id: "faellig", status: "DUE_SOON" },
  { id: "teilbezahlt", status: "PART_PAID" },
  { id: "guthaben", status: "CREDIT_OPEN" },
  { id: "offen", status: "OPEN" },
  { id: "angenommen", status: "ASSUMED_PAID" },
  { id: "bezahlt", status: "PAID" },
  { id: "storniert", status: "CANCELLED" },
];

// ------------------------------------------------------------------ der Kodierer

/**
 * Die Statusse hinter einer Filterauswahl — für die Abfrage an den Server.
 *
 * Die Reihenfolge ist die der Tabelle (der Lebenslauf) und nicht die der Klickfolge: Die
 * Auswahl reist als Abfrageparameter mit, und eine stabile Reihenfolge macht sie im
 * Netzwerkprotokoll lesbar.
 */
export function statusseZuFilter<I extends string, S extends string>(
  aktiv: readonly I[], tabelle: readonly Filtereintrag<I, S>[],
): readonly S[] {
  return tabelle.filter((e) => aktiv.includes(e.id)).map((e) => e.status);
}

/** Der EINE Status hinter einem einfach wählbaren Filter (Angebote, Aufträge). */
export function statusZuFilter<I extends string, S extends string>(
  filter: I, tabelle: readonly Filtereintrag<I, S>[],
): S | undefined {
  return tabelle.find((e) => e.id === filter)?.status;
}

/**
 * Das Adress-Segment einer MEHRFACH-Auswahl — `undefined` heisst „kein Segment", und das
 * ist die Vorgabe „alle eingeschaltet".
 *
 * Alle eingeschaltet ergibt bewusst KEIN Segment: `/rechnungen` IST „alle Rechnungen",
 * genau wie `/verwaltung` das Untermenü ohne Auswahl IST. Ein zweiter Pfad
 * (`/rechnungen/entwurf,festgeschrieben,bezahlt,storniert`) für denselben Bildschirm wäre
 * einer zu viel.
 */
export function segmentFuerFilter<I extends string, S extends string>(
  aktiv: readonly I[], tabelle: readonly Filtereintrag<I, S>[],
): string | undefined {
  if (aktiv.length >= tabelle.length) return undefined;
  if (aktiv.length === 0) return KEINE;
  return tabelle.filter((e) => aktiv.includes(e.id)).map((e) => e.id).join(",");
}

/**
 * Die MEHRFACH-Auswahl hinter einem Adress-Segment — `null` heisst „das ist kein Filter".
 *
 * Der Router entscheidet damit zwischen `/rechnungen/<filter>` und `/rechnungen/<kennung>`
 * und schickt alles Übrige auf „unbekannt": Die Filternamen sind eine geschlossene, kleine
 * Menge, ein anderer Wert ist ein Vertipper.
 *
 * Ein Segment, das ALLE Namen aufzählt, ist GÜLTIG und ergibt die volle Liste — der
 * Router lässt den Filter dann weg (`/rechnungen` IST „alle"), und `useRoute` schreibt die
 * kanonische Form beim Betreten still nach. Es als Fehler zu behandeln schickte ein
 * verständliches Lesezeichen auf „Seite gibt es nicht".
 */
export function filterAusSegment<I extends string, S extends string>(
  segment: string, tabelle: readonly Filtereintrag<I, S>[],
): readonly I[] | null {
  if (segment === KEINE) return [];
  const teile = segment.split(",");
  if (teile.some((t, i) => teile.indexOf(t) !== i)) return null;
  if (!teile.every((t) => tabelle.some((e) => e.id === t))) return null;
  return tabelle.filter((e) => teile.includes(e.id)).map((e) => e.id);
}

/**
 * Die EINFACH-Auswahl hinter einem Adress-Segment (Angebote, Aufträge) — `null` heisst
 * „das ist kein Filter".
 *
 * Diese beiden Bereiche filtern serverseitig über EINEN Status
 * (`VorgangsSucheParams.status`, siehe api-client); ihre Chip-Leiste ist deshalb einfach
 * wählbar, und ihr Adress-Segment trägt genau einen Namen — dieselbe Gestalt, die der
 * Rechnungs-Bereich vor diesem Paket hatte. Ein zusammengesetztes Segment wäre eine
 * Adresse, die mehr verspricht, als die Liste einlösen kann.
 */
export function einzelfilterAusSegment<I extends string, S extends string>(
  segment: string, tabelle: readonly Filtereintrag<I, S>[],
): I | null {
  return tabelle.find((e) => e.id === segment)?.id ?? null;
}

/** Alle Filter einer Tabelle — die Vorgabe „alle Chips an". Als Funktion und nicht als
 *  drei Konstanten daneben, damit die Vorgabe nicht ein zweites Mal dasteht; die
 *  Verwender halten ihr Ergebnis in einer Modulkonstante fest (stabile Identität). */
export function alleFilter<I extends string, S extends string>(
  tabelle: readonly Filtereintrag<I, S>[],
): readonly I[] {
  return tabelle.map((e) => e.id);
}

/** Die Filter hinter einer Statusauswahl (Umkehrung von `statusseZuFilter`), in der Ordnung der Tabelle. */
export function filterForStates<I extends string, S extends string>(
  statusse: readonly S[], tabelle: readonly Filtereintrag<I, S>[],
): readonly I[] {
  return tabelle.filter((e) => statusse.includes(e.status)).map((e) => e.id);
}
